import { motion } from "motion/react";
import type { CSSProperties } from "react";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { t } from "@/lib/i18n";
import type { LinksSection as LinksData } from "@/data/profile";
import type { SectionProps } from "../types";
import { SectionFrame } from "../SectionFrame";

const cardStyle: CSSProperties = {
  position: "relative",
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  gap: 5,
  padding: 18,
  background: "var(--tc-paper)",
  border: "1px solid var(--tc-line-2)",
  borderRadius: 6,
  textDecoration: "none",
  fontFamily: "inherit",
  color: "inherit",
};

const labelStyle: CSSProperties = {
  fontFamily: "var(--tc-mono)",
  fontSize: 10,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--tc-faint)",
};

const valueStyle: CSSProperties = { fontFamily: "var(--tc-serif)", fontSize: 20, color: "var(--tc-ink-strong)" };
const cornerStyle: CSSProperties = { position: "absolute", top: 14, right: 14, color: "var(--tc-faint)" };
const hover = { y: -3, boxShadow: "0 14px 28px -16px rgba(46,40,32,.5)", borderColor: "var(--tc-brass-line)" };

export function LinksSection({ section, number, ctx }: SectionProps<LinksData>) {
  const { lang, copied, copy, presets } = ctx;

  return (
    <SectionFrame number={number} kicker={section.kicker} title={section.title} ctx={ctx}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, maxWidth: 560 }}>
        {section.links.map((link, i) => {
          const isCopy = link.action.kind === "copy";
          const note = isCopy && copied ? (lang === "pt" ? "copiado!" : "copied!") : t(link.note, lang);
          const Icon = isCopy ? (copied ? Check : Copy) : ArrowUpRight;

          const inner = (
            <>
              <Icon size={16} style={cornerStyle} aria-hidden="true" />
              <span style={labelStyle}>{t(link.label, lang)}</span>
              <span style={valueStyle}>{link.value}</span>
              <span style={{ fontSize: 14, color: isCopy ? "var(--tc-brass)" : "var(--tc-muted)" }}>{note}</span>
            </>
          );

          return link.action.kind === "link" ? (
            <motion.a
              key={link.value}
              href={link.action.href}
              target="_blank"
              rel="noopener noreferrer"
              variants={presets.pop(0.16 + i * 0.08)}
              whileHover={hover}
              style={cardStyle}
            >
              {inner}
            </motion.a>
          ) : (
            <motion.button
              key={link.value}
              type="button"
              onClick={() => link.action.kind === "copy" && copy(link.action.value)}
              variants={presets.pop(0.16 + i * 0.08)}
              whileHover={hover}
              style={{ ...cardStyle, cursor: "pointer" }}
            >
              {inner}
            </motion.button>
          );
        })}
      </div>
    </SectionFrame>
  );
}
